import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { push } from 'connected-react-router';
import { compose } from 'lodash/fp';

import NotifyHOC, { IToastNotification } from '../../../HOC/Notifications';
import { IApplicationState } from '../../../reducers';
import {
    clearLocationState,
    interpolate,
    splitPathname
} from '../../../utils/string';
import { getSortedQuestionByProjectId } from '../../../services/question.service';
import {
    getAudienceByProjectID,
    saveAudience
} from '../../../services/sample.service';
import { getDefaultAudience, getDefaultAudienceRule } from './defaultValues';
import Loader from '../../common/Loader/Loader';
import { getConfirmationBox } from '../../common/swal/Sweetalert';
import * as Action from '../../../actions/project.actions';
import {
    setEditedStatus,
    clearEditedStatus,
    sendEvent
} from '../../../services/utils.service';
import { AxiosResponse } from 'axios';
import * as projectService from '../../../services/project.service';
import { ILocation } from '../../../interface/common.interface';
import { alertMessage, titleHeader } from '../../../constants/alertMessage';
import {
    HealthProblemContext,
    Audience as IAudience,
    AudienceRule as IAudienceRule,
    QTYPES,
    Question,
    PanelType,
    PROJECT_STATUS,
    TENANT_LEVEL,
    QuestionSummary,
    ProjectID,
    Project,
    AudienceRuleOption,
    PROJECT_EDIT_STATUS,
    AudienceRule
} from '@nexxt/common/types';
import { sortHealthProblemByQuestionID } from '../../../utils/project';
import { DEMOGRAPHICS_BY_COUNTRY } from '@nexxt/common/demographics/index';
import { validateLanguage } from '@nexxt/common/utils/project';

import { getTenantLevel } from '@nexxt/common/firebase/auth/userDetails';
import UserDefinedAudienceGroup from './UserDefinedAudienceGroup';
import PreDefinedAudienceGroup from './PreDefinedAudienceGroup';
import {
    getQuestionIdsByBuiltInIds,
    getQuestionSummariesWithBuiltInByProjectSettings
} from '../../../utils/question';
import {
    AUDIENCE_RULE_TYPES,
    AUDIENCE_VIEW_MODES,
    PROJECT_EDIT_MODES
} from '../../../interface/project.interface';
import { LOCK_PROJECT } from '../../../constants/project';
import CustomScreenerModal from './AddModal/CustomScreenerModal';
import { QUESTIONS } from '../../../constants/routes';
import { withTranslation } from 'react-i18next';
import { ENG_COUNTRIES } from '@nexxt/common/demographics/types';
import * as countryList from 'react-select-country-list';
import ValidationService from '@nexxt/common/services/ValidationService';
import AutoSavingService from '../../../utils/AutoSavingService';
import {
    SVC_EVENT_NAME,
    SVC_EVENT_TYPE,
    SVC_NAMESPACE_TYPE
} from '@nexxt/common/constants';

export interface AudienceProps {
    projectId: string;
    questionSummaries: QuestionSummary[];
    toastNotification: IToastNotification;
    currentProject: any;
    editEnabled: boolean;
    isEdited: boolean;
    editMode: PROJECT_EDIT_MODES;
    editStatus: PROJECT_EDIT_STATUS;
    changePath: (path: string) => void;
    getAudienceByProjectID: (projectId: string) => Promise<any>;
    saveAudience: (
        projectId: string,
        audienceRuless: any,
        disablePanel: boolean
    ) => Promise<any>;
    projectHealth: any;
    location: ILocation;
    history: any;
    viewMode: string;
    setProject: (newProject: any) => void;
    getSortedQuestionByProjectId: (projectId: number) => Promise<any>;
    setEditedStatus: (
        type: string,
        status?: PROJECT_EDIT_STATUS
    ) => Promise<any>;
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) => Promise<any>;
    t?: (text: string, options?: Record<string, boolean>) => string; // translate function
    updateProject: (projectID: ProjectID, project: Project) => Promise<any>;
}

export interface AudienceState {
    audience: IAudience;
    isLoading: boolean;
    isSaving: boolean;
    isAdd: boolean;
    isEdit: boolean;
    addAudienceRule: IAudienceRule;
    editAudienceRule: IAudienceRule;
    editedAudienceRuleIndex: number;
    questionList: QuestionSummary[];
    showCustomScreenerModal: boolean;
    customScreenerModalType: AUDIENCE_RULE_TYPES;
    currentKeyName: AUDIENCE_RULE_TYPES;
    questionIdModal: number;
    showError: boolean;
    pageMode: AUDIENCE_VIEW_MODES;
    templateSettings: any[]; // @TODO: typing!
    countryValid: boolean;
    AudienceRuleIndices: any;
    autoSavingService: AutoSavingService<IAudience>;
}

const L = [];
class Audience extends React.Component<AudienceProps, AudienceState> {
    // this will be updated on saving each audience rule so that this can replace on cancelling "edit audience rule"
    private previousAudience;
    private validationService;
    constructor(props) {
        super(props);
        this.validationService = new ValidationService();
        const defaultAudience = getDefaultAudience(
            this.props.projectId,
            getTenantLevel()
        );
        this.state = {
            audience: defaultAudience,
            addAudienceRule: getDefaultAudienceRule(),
            editAudienceRule: null,
            isLoading: true,
            isSaving: false,
            isAdd: false,
            isEdit: false,
            editedAudienceRuleIndex: -1,
            questionList: getQuestionSummariesWithBuiltInByProjectSettings(
                this.props.questionSummaries.filter(
                    (q) => !(q.builtInID && q.isAudience)
                ),
                this.props.currentProject,
                false
            ),
            showCustomScreenerModal: false,
            customScreenerModalType: null,
            currentKeyName: null,
            questionIdModal: null,
            showError: false,
            pageMode:
                getTenantLevel() === TENANT_LEVEL.SELF_SERVICE
                    ? AUDIENCE_VIEW_MODES.SELF_SERVICE
                    : AUDIENCE_VIEW_MODES.FULL_SERVICE,
            templateSettings: null,
            countryValid: false,
            AudienceRuleIndices: [],
            autoSavingService: new AutoSavingService(
                defaultAudience,
                `project | audience`,
                false,
                this.props.editStatus,
                this.props.setEditedStatus,
                this.props.clearEditedStatus
            )
        };
    }

    async componentDidMount() {
        const promises = [
            await this.props.getAudienceByProjectID(this.props.projectId)
        ];

        if (Object.keys(this.props.currentProject).length == 0) {
            promises.push(await this.fetchProjectById());
        }
        try {
            const responses = await Promise.all(promises);
            let audience: IAudience = getDefaultAudience(
                this.props.projectId,
                getTenantLevel()
            );
            if (responses[0]) {
                audience = responses[0];
                this.previousAudience = _.cloneDeep(audience);
            }
            const builtInIndices = {};
            const customScreenerIndices = {};
            audience.builtInAudienceRules.forEach((rule, index) => {
                builtInIndices[rule.selectedQuestion.questionId] = index;
            });
            audience.customAudienceRules.forEach((rule, index) => {
                customScreenerIndices[rule.selectedQuestion.questionId] = index;
            });
            const countryValid =
                validateLanguage(this.props.currentProject) &&
                this.props.currentProject.countries?.length === 1;
            this.state.autoSavingService.setData(audience);
            this.setState({
                audience,
                isLoading: false,
                countryValid,
                AudienceRuleIndices: [builtInIndices, customScreenerIndices]
            });
        } catch (e) {
            console.error(e);
            this.setState({
                isLoading: false
            });
        }
        const locationState = this.props.location.state;
        if (locationState?.questionID) {
            this.setState({
                showCustomScreenerModal: true,
                questionIdModal: parseInt(locationState?.questionID)
            });
        }
        this.getTemplateSettings();

        this.state.autoSavingService.setAutoSavingInterval(this.onSaveAudience);
    }

    componentDidUpdate(prevProps: AudienceProps, prevState: AudienceState) {
        this.state.autoSavingService?.setData(this.state.audience);

        if (prevProps.questionSummaries !== this.props.questionSummaries) {
            const newQuestionSummaries = this.props.questionSummaries.filter(
                (q) => !(q.builtInID && q.isAudience)
            );
            this.setState({
                questionList: getQuestionSummariesWithBuiltInByProjectSettings(
                    newQuestionSummaries,
                    this.props.currentProject,
                    false
                )
            });
        }

        if (!_.isEqual(prevProps.editStatus, this.props.editStatus)) {
            this.state.autoSavingService.setCurrentEditStatus(
                this.props.editStatus
            );
        }
        if (prevState.isSaving !== this.state.isSaving) {
            this.state.autoSavingService.setIsSaving(this.state.isSaving);
        }
    }

    componentWillUnmount() {
        this.state.autoSavingService &&
            this.state.autoSavingService.clearAutoSaving();
    }

    setEditFlag = () => {
        this.state.autoSavingService.setEditing();

        this.state.autoSavingService.setEditFlag({ isError: false });
    };

    fetchProjectById = async () => {
        try {
            const projectId = splitPathname(this.props.location.pathname);

            const response: AxiosResponse = await projectService.fetchProject(
                projectId
            );

            const projectDetails = response.data.data;

            const data = {
                ...projectDetails,
                projectId: projectId,
                packageID: response.data.packageID
            };

            this.props.setProject(data);
            return data;
        } catch (error) {
            this.props.toastNotification.error(
                alertMessage.UNABLE_FETCH_PROJECT
            );
        }
    };

    getTemplateSettings = () => {
        const { editMode, currentProject } = this.props;
        try {
            const templateSettings =
                LOCK_PROJECT[editMode as PROJECT_EDIT_MODES][
                    currentProject?.templateCode
                ]?.audience;

            this.setState({ templateSettings });
        } catch (err) {}
    };

    /** For custom screener modal */
    openCustomScreenerModal = (screenerType: AUDIENCE_RULE_TYPES) => {
        this.setState({
            showCustomScreenerModal: true,
            customScreenerModalType: screenerType
        });
    };

    closeCustomScreenerModal = () => {
        this.setState({
            showCustomScreenerModal: false
        });
        clearLocationState(this.props.history, this.props.location);
    };

    addExistingScreener = (
        selectedQuestion,
        keyname = AUDIENCE_RULE_TYPES.CUSTOM_RULE
    ) => {
        this.setState({
            isAdd: true,
            addAudienceRule: {
                ...this.state.addAudienceRule,
                selectedQuestion
            },
            currentKeyName: keyname,
            showCustomScreenerModal: false
        });
        clearLocationState(this.props.history, this.props.location);
    };
    /** For built in questions that require users to add their own options */
    onAddDynamicQuestion = (
        selectedQuestion: any,
        audienceRuleOptions: AudienceRuleOption[],
        keyname: AUDIENCE_RULE_TYPES = AUDIENCE_RULE_TYPES.CUSTOM_RULE
    ): void => {
        this.setState(
            {
                addAudienceRule: {
                    audienceRuleOptions,
                    selectedQuestion
                },
                currentKeyName: keyname
            },
            () => {
                this.onSaveAudienceRule(-1, keyname);
            }
        );
    };

    onUpdateDynamicQuestion = (
        audienceRuleOptions: AudienceRuleOption[],
        keyname: AUDIENCE_RULE_TYPES = AUDIENCE_RULE_TYPES.CUSTOM_RULE,
        index: number
    ): void => {
        const currentAudienceRules = [...this.state.audience[keyname]];
        const updatedAudienceRule = {
            ...currentAudienceRules[index],
            audienceRuleOptions
        };
        currentAudienceRules.splice(index, 1, updatedAudienceRule);
        this.setState(
            (prevState) => {
                return {
                    audience: {
                        ...prevState.audience,
                        [keyname]: currentAudienceRules
                    }
                };
            },
            () => {
                this.onSaveAudienceRule(index, keyname);
            }
        );
    };

    /** Handle Total sample size */
    handleProjectSettings = (
        keyName: string,
        value: any
        // event: React.ChangeEvent<HTMLInputElement>
    ) => {
        this.setState(
            (prevState) => ({
                audience: {
                    ...prevState.audience,
                    [keyName]: value
                }
            }),
            this.setEditFlag
        );
    };

    mappingPredefinedAudience = () => {
        const { predefinedAudience } = this.state.audience;
        const questionIdObject = getQuestionIdsByBuiltInIds(
            this.props.questionSummaries
        );
        const mappedPredefinedAudience = predefinedAudience.map((pAudience) => {
            const terminates = pAudience.terminates?.map((terminate) => {
                return {
                    ...terminate,
                    questionId: questionIdObject[terminate.builtInId] as number
                };
            });
            const quotas = pAudience.quotas?.map((quota) => {
                const quotaRule = {
                    ...quota.quotaRule,
                    questionId: questionIdObject[
                        quota.quotaRule.builtInId
                    ] as number
                };
                return { ...quota, quotaRule };
            });
            return { ...pAudience, quotas, terminates };
        });

        return {
            ...this.state.audience,
            predefinedAudience: mappedPredefinedAudience
        };
    };

    /** Add new audienceRules(Built-in demographic question) */
    onClickAddBuiltInDemographic = (keyName) => {
        if (this.state.isAdd || this.state.isEdit) {
            alert('save current changes first');
        } else {
            this.setState({
                isAdd: true,
                currentKeyName: keyName
            });
        }
    };

    onDeleteAudienceRule = async (index, keyName) => {
        const { audience } = this.state;
        const audienceRules = [...audience[keyName]];
        const questionID = audienceRules[index].selectedQuestion.questionId;
        if (keyName === AUDIENCE_RULE_TYPES.CUSTOM_RULE) {
            await this.onDeleteCustomScreener(questionID, index, audienceRules);
        } else {
            await this.onDeleteBuiltInAudienceRule(
                keyName,
                questionID,
                index,
                audienceRules
            );
        }
        // overwrite previousAudience with new (saved) audience
        this.previousAudience = _.cloneDeep(this.state.audience);
    };

    onDeleteBuiltInAudienceRule = async (
        keyName: string,
        questionID: number,
        index: number,
        audienceRules: IAudienceRule[]
    ) => {
        const { audience } = this.state;
        try {
            const result = await getConfirmationBox({
                message: alertMessage.CONFIRM_DELETE_ITEM,
                showCloseButton: true,
                confirmButtonColor: '',
                title: alertMessage.DELETE_ITEM
            });
            if (result.value) {
                // Add questionID into deleteQuestionIDs array if the question has been created
                questionID > 0 && audience.deleteQuestionIDs.push(questionID);
                audienceRules.splice(index, 1);

                this.setState(
                    (prevState) => ({
                        audience: {
                            ...prevState.audience,
                            [keyName]: audienceRules
                        }
                    }),
                    this.setEditFlag
                );
            }
        } catch (e) {}
    };
    onDeleteAllBuiltInAudienceRule = async () => {
        const { audience } = this.state;
        const { builtInAudienceRules } = audience;
        builtInAudienceRules.forEach(({ selectedQuestion }) => {
            Number(selectedQuestion.questionId) > 0 &&
                this.state.audience.deleteQuestionIDs.push(
                    selectedQuestion.questionId
                );
        });
        this.setState(
            (prevState) => ({
                audience: {
                    ...prevState.audience,
                    [AUDIENCE_RULE_TYPES.BUILT_IN_RULE]: []
                }
            }),
            this.setEditFlag
        );
    };

    onDeleteCustomScreener = async (
        questionID: number,
        index: number,
        audienceRules: IAudienceRule[]
    ) => {
        const { audience } = this.state;
        try {
            const result = await getConfirmationBox({
                message: alertMessage.CONFIRM_DELETE_QUESTION_IN_AUDIENCE,
                denyButtonText: 'keep question',
                showCloseButton: true,
                showDenyButton: questionID > 0 ? true : false,
                showCancelButton: true,
                confirmButtonColor: '',
                title: alertMessage.DELETE_ITEM
            });
            if (result.isConfirmed) {
                questionID > 0 && audience.deleteQuestionIDs.push(questionID);
                audienceRules.splice(index, 1);

                this.setState(
                    (prevState) => ({
                        audience: {
                            ...prevState.audience,
                            customAudienceRules: audienceRules
                        }
                    }),
                    this.setEditFlag
                );
            }
            if (result.isDenied) {
                audience.switchScreenerQuestionIDs.push(questionID);
                audienceRules.splice(index, 1);
                this.setState(
                    (prevState) => ({
                        audience: {
                            ...prevState.audience,
                            customAudienceRules: audienceRules
                        }
                    }),
                    this.setEditFlag
                );
            }
        } catch (e) {}
    };

    onEditAudienceRule = (index, keyName) => {
        if (this.state.isEdit || this.state.isAdd) {
            alert('save current changes first');
            return;
        }
        const audienceRules = [...this.state.audience[keyName]];
        const currentAudienceRule = { ...audienceRules[index] };
        currentAudienceRule.audienceRuleOptions =
            currentAudienceRule.audienceRuleOptions.map((q) => ({
                ...q
            }));
        currentAudienceRule.edit = true;
        audienceRules.splice(index, 1, currentAudienceRule);
        const copyAudienceRule = this.state.audience[keyName][index];
        this.setState(
            (prevState) => ({
                isEdit: true,
                editedAudienceRuleIndex: index,
                editAudienceRule: copyAudienceRule,
                audience: {
                    ...prevState.audience,
                    [keyName]: audienceRules
                }
            }),
            () => this.state.autoSavingService.setEditing()
        );
    };

    onSaveAudienceRule = (index, keyName) => {
        const audienceRules = [...this.state.audience[keyName]];
        const currentAudienceRule = { ...audienceRules[index] };

        delete currentAudienceRule.edit;
        audienceRules.splice(index, 1, currentAudienceRule);

        if (index === -1) {
            if (this.state.addAudienceRule) {
                const audienceRule = {
                    ...this.state.addAudienceRule,
                    isEdit: false
                };

                if (
                    (audienceRule.selectedQuestion as Partial<Question>)
                        .type === QTYPES.SPECIALS
                ) {
                    keyName = AUDIENCE_RULE_TYPES.SPECIAL_RULE;
                }
                this.setState((prevState: AudienceState) => {
                    return {
                        isAdd: false,
                        addAudienceRule: getDefaultAudienceRule(),
                        audience: {
                            ...prevState.audience,
                            [keyName]: [
                                ...prevState.audience[keyName],
                                { ...audienceRule, edit: false }
                            ]
                        }
                    };
                }, this.setEditFlag);
            }
        } else {
            this.setState((prevState) => {
                return {
                    isEdit: false,
                    editAudienceRule: null,
                    editedAudienceRuleIndex: -1,
                    audience: {
                        ...prevState.audience,
                        [keyName]: audienceRules
                    }
                };
            }, this.setEditFlag);
        }
        // overwrite previousAudience with new (saved) audience
        this.previousAudience = _.cloneDeep(this.state.audience);
    };

    /*
     * Cancel the creation of a new audience rule, either reinstating
     * previous audience, or resetting to default if none.
     */
    onCancelAudienceRule = (index, keyName) => {
        if (index === -1) {
            // no previous audience: reset to default
            this.setState(
                {
                    isAdd: false,
                    addAudienceRule: getDefaultAudienceRule()
                },
                this.setEditFlag
            );
        } else {
            // Check for lingering edit flag
            let flag = false;
            const prevAud = [...this.previousAudience[keyName]];
            prevAud.forEach((audRule) => {
                if (audRule.edit) {
                    delete audRule.edit;
                    flag = true;
                }
            });

            // reinstate previously saved audience
            this.setState(
                (prevState) => {
                    return {
                        isEdit: false,
                        editAudienceRule: null,
                        editedAudienceRuleIndex: -1,
                        currentKeyName: null,
                        audience: {
                            ...this.previousAudience,
                            [keyName]: prevAud
                        }
                    };
                },
                () => {
                    if (flag) {
                        // overwrite previousAudience without edit flag
                        this.previousAudience = _.cloneDeep(
                            this.state.audience
                        );
                    }
                    this.props.clearEditedStatus();
                }
            );
        }
    };

    onUpdateAudience = (
        index,
        audienceRule: IAudienceRule,
        keyName,
        validateRule?
    ) => {
        if (index === -1) {
            this.setState(
                {
                    addAudienceRule: { ...audienceRule }
                },
                validateRule
            );
        } else {
            this.setState(
                (prevState) => {
                    const audienceRules = [
                        ...(prevState.audience[keyName] || [])
                    ];
                    audienceRules.splice(index, 1, audienceRule);
                    return {
                        audience: {
                            ...prevState.audience,
                            [keyName]: audienceRules
                        }
                    };
                },
                () => {
                    validateRule?.();
                }
            );
        }
    };

    onSaveAudience = async () => {
        const { totalSampleSize, lengthOfInterview } = this.state.audience;
        const { editStatus } = this.props;
        if (this.state.isAdd) {
            editStatus !== PROJECT_EDIT_STATUS.EDIT_REQUIRED &&
                this.props.clearEditedStatus(PROJECT_EDIT_STATUS.EDIT_REQUIRED);
        } else {
            try {
                this.setState({
                    isSaving: true
                });
                let currentEditIndex;

                let audience = this.state.audience;
                if (this.state.pageMode === AUDIENCE_VIEW_MODES.SELF_SERVICE) {
                    audience = this.mappingPredefinedAudience();
                }
                const disablePanel = !(
                    audience.panelType === PanelType.INCA &&
                    this.props.currentProject?.status === PROJECT_STATUS.DRAFT
                );

                const response = await this.props.saveAudience(
                    this.props.projectId,
                    audience,
                    disablePanel
                );

                if (this.state.isEdit) {
                    const getEditIndexReducer = (obj, curr, index) => {
                        return { ...obj, [index]: curr?.edit };
                    };
                    currentEditIndex = {
                        builtInAudienceRules:
                            this.state.audience.builtInAudienceRules.reduce(
                                getEditIndexReducer,
                                {}
                            ),
                        customAudienceRules:
                            this.state.audience.customAudienceRules.reduce(
                                getEditIndexReducer,
                                {}
                            )
                    };
                }
                this.props.setProject({
                    ...this.props.currentProject,
                    totalSampleSize,
                    lengthOfInterview,
                    panelSettings: {
                        ...this.props.currentProject.panelSettings,
                        panelType: audience.panelType,
                        panels: audience.panels
                    }
                });

                this.state.autoSavingService.setSyncedData(response.data);
                this.previousAudience = _.cloneDeep(response.data);
                this.props.clearEditedStatus();

                const markAudienceEditing = (
                    type,
                    audienceRule: AudienceRule,
                    index
                ) => {
                    return {
                        ...audienceRule,
                        edit: currentEditIndex?.[type]?.[index] || false
                    };
                };

                this.setState((prevState) => ({
                    isSaving: false,
                    audience: {
                        ...prevState.audience,
                        // ...response.data,
                        // lengthOfInterview:
                        //     this.state.audience.lengthOfInterview,
                        // panelType: this.state.audience.panelType,
                        // totalSampleSize: this.state.audience.totalSampleSize,
                        builtInAudienceRules:
                            response.data.builtInAudienceRules?.map(
                                (audienceRule, index) =>
                                    markAudienceEditing(
                                        'builtInAudienceRules',
                                        audienceRule,
                                        index
                                    )
                            ),
                        customAudienceRules:
                            response.data.customAudienceRules?.map(
                                (audienceRule, index) =>
                                    markAudienceEditing(
                                        'customAudienceRules',
                                        audienceRule,
                                        index
                                    )
                            )
                    },
                    isEdit: false
                }));

                // this.props.toastNotification.success(
                //     alertMessage.QUOTA_SAVE_SUCCESS
                // );
                // if (
                //     this.props.editMode === PROJECT_EDIT_MODES.ENABLED &&
                //     this.state.audience.customAudienceRules.length &&
                //     this.state.pageMode === AUDIENCE_VIEW_MODES.FULL_SERVICE &&
                //     this.state.audience?.panelType === PanelType.INCA
                // ) {
                //     this.props.toastNotification.info(
                //         alertMessage.QUOTA_SAVE_SUCCESS_INCIDENCE_WARNING
                //     );
                // }
            } catch (e) {
                this.setState({
                    isSaving: false,
                    showError: true
                });
                this.props.toastNotification.error(
                    alertMessage.QUOTA_SAVE_FAIL
                );
                this.props.clearEditedStatus(PROJECT_EDIT_STATUS.FAILED);
            }
        }
    };

    /**
     * Checks if (total sample size, and length of interview are empty).
     * If PanelType is Self provided, will only check sample size.
     * If PanelType is inca Panel, will check both.
     * returns true if fails check -> returns false if check passes
     */
    checkPanelRequirements = () => {
        const { totalSampleSize, lengthOfInterview } = this.state.audience;
        const panelType = this.state.audience?.panelType === PanelType.INCA;
        if (panelType) {
            return (
                this.validationService.isEmpty(totalSampleSize) ||
                !totalSampleSize ||
                this.validationService.isEmpty(lengthOfInterview) ||
                !lengthOfInterview
            );
        } else {
            return (
                this.validationService.isEmpty(totalSampleSize) ||
                !totalSampleSize
            );
        }
    };
    getQuestionList = (keyName) => {
        const { t } = this.props;
        const { countries, languages } = this.props.currentProject;
        const { panelType } = this.state.audience;
        let questionList = [];

        if (keyName === AUDIENCE_RULE_TYPES.BUILT_IN_RULE) {
            if (
                countries?.length === 1 &&
                validateLanguage(this.props.currentProject) &&
                panelType === PanelType.INCA
            ) {
                const selectedCountryValue = countries[0].value;
                questionList = DEMOGRAPHICS_BY_COUNTRY(
                    languages,
                    selectedCountryValue,
                    t,
                    {
                        questionTexts: true,
                        options: true
                    }
                );
            }
            questionList = questionList.length
                ? _.sortBy(questionList, 'title')
                : questionList;
        } else if (keyName === AUDIENCE_RULE_TYPES.CUSTOM_RULE) {
            questionList = this.state.questionList;
        }
        const referenceQuestionValues = this.state.audience[keyName].map(
            (audienceRule) => audienceRule.selectedQuestion.value
        );

        // Filter out the existing audience referenece questions
        const filterQuestion = (q) =>
            !referenceQuestionValues.includes(q.value);

        const filteredQuestionList = questionList.filter((q) => {
            if (q['options']?.length) {
                q.options = q.options.filter((c) => filterQuestion(c));
            }

            if (q.type === QTYPES.SYSTEM_METADATA && q?.options?.length === 0) {
                return false;
            }
            return filterQuestion(q);
        });

        return filteredQuestionList;
    };

    renderScreenerModal = () => {
        switch (true) {
            case this.state.customScreenerModalType ===
                AUDIENCE_RULE_TYPES.CUSTOM_RULE:
                return (
                    <CustomScreenerModal
                        showCustomScreenerModal={
                            this.state.showCustomScreenerModal
                        }
                        screenerType={this.state.customScreenerModalType}
                        closeCustomScreenerModal={this.closeCustomScreenerModal}
                        customAudienceRules={
                            this.state.audience.customAudienceRules
                        }
                        questionList={this.getQuestionList(
                            AUDIENCE_RULE_TYPES.CUSTOM_RULE
                        )}
                        questionId={this.state.questionIdModal}
                        onDone={this.addExistingScreener}
                        changePathToQuestions={this.changePathToQuestions}
                    />
                );
            default:
                return (
                    <CustomScreenerModal
                        showCustomScreenerModal={
                            this.state.showCustomScreenerModal
                        }
                        screenerType={this.state.customScreenerModalType}
                        closeCustomScreenerModal={this.closeCustomScreenerModal}
                        customAudienceRules={
                            this.state.audience.builtInAudienceRules
                        }
                        questionList={this.getQuestionList(
                            AUDIENCE_RULE_TYPES.BUILT_IN_RULE
                        )}
                        questionId={this.state.questionIdModal}
                        onDone={this.addExistingScreener}
                        changePathToQuestions={this.changePathToQuestions}
                    />
                );
        }
    };

    changePathToQuestions = () => {
        this.props.changePath(
            interpolate(QUESTIONS, {
                projectId: this.props.currentProject.id
            })
        );
    };
    confirmCountryChange = async (countries) => {
        // if confirmed wipe built-in targeting options
        // change country
        try {
            if (this.state.audience?.panelType === PanelType.INCA) {
                if (this.props.currentProject.countries?.length > 0) {
                    const result = await getConfirmationBox({
                        message: alertMessage.CONFIRM_SWITCHING_COUNTRIES,
                        showCloseButton: true,
                        confirmButtonColor: '',
                        title: titleHeader.SWITCH_COUNTRY
                    });
                    if (!result.value) {
                        return;
                    }
                    this.setState({
                        isLoading: true
                    });
                    this.onDeleteAllBuiltInAudienceRule();
                }
            } else if (
                this.state.audience?.panelType === PanelType.SELF_PROVIDED
            ) {
                this.setState({
                    isLoading: true
                });
            }
            this.updateProjectCountries(countries);
            await this.setState({
                isLoading: false
            });
        } catch (e) {}
    };

    /**
     * Responsible for toggling inca panel sourcing
     * If confirmed will delete all targeting options
     */
    confirmPanelSwitching = async (panelType) => {
        // Check for unsaved data before proceeding
        if (this.state.isAdd) {
            alert('save current changes first!');
        } else {
            const panel = panelType.value === PanelType.INCA;
            try {
                if (!panel) {
                    if (this.state.audience.builtInAudienceRules.length > 0) {
                        const result = await getConfirmationBox({
                            message: alertMessage.CONFIRM_SWITCH_PANEL_SOURCING,
                            showCloseButton: true,
                            confirmButtonColor: '',
                            title: titleHeader.SWITCH_PANEL_SOURCING
                        });
                        if (!result.value) {
                            return;
                        }
                    }
                    this.setState({
                        isLoading: true
                    });
                    await this.onDeleteAllBuiltInAudienceRule();
                    await this.setState({
                        isLoading: false
                    });
                    this.handleProjectSettings(
                        'panelType',
                        panel ? PanelType.INCA : PanelType.SELF_PROVIDED
                    );
                    // this.onSaveAudience();
                } else {
                    const { countries } = this.props.currentProject;
                    if (countries.length > 0) {
                        const hasMultipleCountries = countries.length > 1;
                        const currentCountry =
                            this.props.currentProject.countries[0];
                        const isSupportedCountry = countryList()
                            .getData()
                            .filter((country) =>
                                ENG_COUNTRIES.includes(country.value)
                            )
                            .some(
                                (country) =>
                                    country.value === currentCountry.value
                            );

                        if (hasMultipleCountries || !isSupportedCountry) {
                            let message;
                            if (hasMultipleCountries && !isSupportedCountry) {
                                message =
                                    alertMessage.CONFIRM_SWITCH_PANEL_COUNTRY_NOT_SUPPORTED_MULTIPLE;
                            } else if (hasMultipleCountries) {
                                message =
                                    alertMessage.CONFIRM_SWITCH_PANEL_MULTIPLE_COUNTRIES;
                            } else if (!isSupportedCountry) {
                                message =
                                    alertMessage.CONFIRM_SWITCH_PANEL_COUNTRY_NOT_SUPPORTED;
                            }
                            const result = await getConfirmationBox({
                                message: message,
                                showCloseButton: true,
                                confirmButtonColor: '',
                                title: titleHeader.SWITCH_PANEL_SOURCING
                            });
                            if (!result.value) {
                                return;
                            }
                        }
                        this.setState({
                            isLoading: true
                        });
                        if (countries.length === 1) {
                            !isSupportedCountry &&
                                this.updateProjectCountries([]);
                            // -> switch panel type
                        } else if (countries.length > 1) {
                            isSupportedCountry
                                ? this.updateProjectCountries([currentCountry])
                                : this.updateProjectCountries([]);
                            // -> switch panel type
                        }
                    }
                    this.handleProjectSettings(
                        'panelType',
                        panel ? PanelType.INCA : PanelType.SELF_PROVIDED
                    );
                    this.setState({
                        isLoading: false
                    });
                }
            } catch (e) {
                console.error(e);
            }
        }
    };
    updateProjectCountries = async (countries) => {
        const data = {
            ...this.props.currentProject,
            countries
        };
        await this.props.updateProject(this.props.currentProject.id, data);
        this.props.setProject(data);
    };

    updatePanelSettings = (index, panel) => {
        const { panels } = this.state.audience;
        let newPanelSettings;
        if (panel) {
            newPanelSettings = [
                ...panels.slice(0, index),
                panel,
                ...panels.slice(index + 1)
            ];
        } else {
            newPanelSettings = [
                ...panels.slice(0, index),
                ...panels.slice(index + 1)
            ];
        }
        this.setState(
            (prevState) => ({
                audience: { ...prevState.audience, panels: newPanelSettings }
            }),
            this.setEditFlag
        );
    };
    disableByFieldName = (fieldName = '') => {
        const { editEnabled, editMode } = this.props;
        const { templateSettings } = this.state;

        if (!editEnabled) {
            const lockMode =
                editMode === PROJECT_EDIT_MODES.REPUBLISH
                    ? [
                          'sample-size',
                          'self-provided-panel',
                          'edit-custom-screener'
                      ]
                    : templateSettings || ['all'];
            return !lockMode.includes(fieldName);
        }

        return false;
    };
    render() {
        const {
            showCustomScreenerModal,
            showError,
            isLoading,
            isAdd,
            isEdit,
            audience,
            pageMode,
            templateSettings
        } = this.state;
        const { currentProject } = this.props;

        // note: enableLoi only currently used for the PreDefinedAudienceGroup
        // if not enabled, the loi value will ultimately be set to 0 in db
        const loiSettings = templateSettings?.find((s) => s['title'] == 'loi');
        const enableLoi = loiSettings ? loiSettings.enabled : true;
        const isIncaPanelEnabled = audience?.panelType === PanelType.INCA;
        return (
            <div className="audience-wrapper">
                <div className="audience">
                    {showCustomScreenerModal && this.renderScreenerModal()}

                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            {pageMode === AUDIENCE_VIEW_MODES.SELF_SERVICE ? (
                                <div style={{ margin: '0 20px' }}>
                                    <PreDefinedAudienceGroup
                                        audience={audience}
                                        showError={showError}
                                        editEnabled={
                                            !this.disableByFieldName('all')
                                        }
                                        handleProjectSettings={
                                            this.handleProjectSettings
                                        }
                                        enableLoi={enableLoi}
                                        validationService={
                                            this.validationService
                                        }
                                    />
                                </div>
                            ) : (
                                <UserDefinedAudienceGroup
                                    currentKeyName={this.state.currentKeyName}
                                    isAdd={isAdd}
                                    isIncaPanelEnabled={isIncaPanelEnabled}
                                    isEdit={isEdit}
                                    isEdited={this.props.isEdited}
                                    isSaving={this.state.isSaving}
                                    addAudienceRule={this.state.addAudienceRule}
                                    audience={audience}
                                    previousAudience={this.previousAudience}
                                    audienceRuleIndices={
                                        this.state.AudienceRuleIndices
                                    }
                                    questionList={this.state.questionList}
                                    showError={showError}
                                    disableByFieldName={this.disableByFieldName}
                                    currentProject={currentProject}
                                    projectHealth={this.props.projectHealth}
                                    onAddDynamicQuestion={
                                        this.onAddDynamicQuestion
                                    }
                                    onUpdateDynamicQuestion={
                                        this.onUpdateDynamicQuestion
                                    }
                                    getQuestionList={this.getQuestionList}
                                    confirmPanelSwitching={
                                        this.confirmPanelSwitching
                                    }
                                    handleProjectSettings={
                                        this.handleProjectSettings
                                    }
                                    onCancelAudienceRule={
                                        this.onCancelAudienceRule
                                    }
                                    onDeleteAudienceRule={
                                        this.onDeleteAudienceRule
                                    }
                                    onEditAudienceRule={this.onEditAudienceRule}
                                    onUpdateAudience={this.onUpdateAudience}
                                    onSaveAudienceRule={this.onSaveAudienceRule}
                                    onSaveAudience={this.onSaveAudience}
                                    onCountryChange={this.confirmCountryChange}
                                    openCustomScreenerModal={
                                        this.openCustomScreenerModal
                                    }
                                    updatePanelSettings={
                                        this.updatePanelSettings
                                    }
                                    validationService={this.validationService}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = ({
    questionReducer,
    router,
    projectReducer,
    utilsReducer
}: IApplicationState) => {
    const projectId = splitPathname(router.location.pathname);
    const { currentProject, editEnabled, editMode } = projectReducer;
    return {
        projectId,
        questionSummaries: questionReducer.questionSummaries[projectId] || L,
        currentProject,
        editEnabled: editEnabled['audience'],
        editMode,
        projectHealth: sortHealthProblemByQuestionID(
            projectReducer.projectHealth,
            [HealthProblemContext.AUDIENCE]
        ),
        isEdited: utilsReducer.isEdited,
        viewMode: utilsReducer.viewMode,
        editStatus: utilsReducer.editStatus
    };
};

const mapDispatchToProps = (dispatch) => ({
    getAudienceByProjectID: (projectId: string) =>
        dispatch(getAudienceByProjectID(projectId)),
    saveAudience: (
        projectId: string,
        audienceRules: any,
        disablePanel: boolean
    ) => dispatch(saveAudience(projectId, audienceRules, disablePanel)),
    setProject: (project: any) => {
        dispatch(Action.setProject(project));
    },
    getSortedQuestionByProjectId: (projectId: number) =>
        dispatch(getSortedQuestionByProjectId(projectId)),
    setEditedStatus: (type: string, status?: PROJECT_EDIT_STATUS) =>
        dispatch(setEditedStatus(type, status)),
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) =>
        dispatch(clearEditedStatus(status)),
    changePath: (path) => {
        dispatch(push(path));
    },
    updateProject: (projectID: ProjectID, project: Project) =>
        dispatch(projectService.updateProject(projectID, project))
});

export default NotifyHOC(
    compose(
        withTranslation(),
        connect(mapStateToProps, mapDispatchToProps)
    )(Audience)
);
